import React, { useCallback } from 'react';
import { MCarouselControlsProps, MCarouselControlsType } from './MCarouselControls.types';

const WRAPPER_CLASS_MAP: Record<MCarouselControlsType, string> = {
  [MCarouselControlsType.Dots]: 'm-carousel-control-dot',
  [MCarouselControlsType.DotsWithOverlay]: 'm-carousel-control-dot',
  [MCarouselControlsType.Bar]: 'm-carousel-control-bar',
};

export const MCarouselControls: React.FC<MCarouselControlsProps> = ({
  type = MCarouselControlsType.Dots,
  imageOverlay = false,
  totalSlides = 5,
  currentSlide = 0,
  onNext,
  onPrev,
  className = '',
}) => {
  const safeCurrentSlide = Math.max(0, Math.min(currentSlide, totalSlides - 1));
  const isFirstSlide = safeCurrentSlide === 0;
  const isLastSlide = safeCurrentSlide === totalSlides - 1;
  const wrapperClass = `${WRAPPER_CLASS_MAP[type]}${className ? ` ${className}` : ''}`;

  const handlePrev = useCallback(() => {
    if (!isFirstSlide && onPrev) {
      onPrev();
    }
  }, [isFirstSlide, onPrev]);

  const handleNext = useCallback(() => {
    if (!isLastSlide && onNext) {
      onNext();
    }
  }, [isLastSlide, onNext]);

  const handleKeyDown = useCallback(
    (direction: 'left' | 'right') => (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        direction === 'left' ? handlePrev() : handleNext();
      }
    },
    [handlePrev, handleNext]
  );

  const renderDots = () => {
    if (totalSlides < 1) return null;
    const imageOverlayClass = imageOverlay ? ' image-overlay' : '';

    return (
      <div className="m-carousel-control-dot-container" role="tablist">
        {Array.from({ length: totalSlides }, (_, index) => (
          <div key={index} className="m-carousel-control-dot-wrapper">
            <div
              role="tab"
              className={`m-carousel-control-dot-indicator${
                index === safeCurrentSlide ? ' is-active' : ''
              }${imageOverlayClass}`}
              aria-selected={index === safeCurrentSlide}
              aria-current={index === safeCurrentSlide ? 'true' : undefined}
              data-testid="carousel-dot"
            />
          </div>
        ))}
      </div>
    );
  };

  const renderBar = () => {
    if (totalSlides < 1) return null;

    return (
      <div className="m-carousel-control-bar-container" role="tablist">
        {Array.from({ length: totalSlides }, (_, index) => (
          <div
            key={index}
            role="tab"
            className={`m-carousel-control-bar-segment${index === safeCurrentSlide ? ' is-active' : ''}`}
            aria-selected={index === safeCurrentSlide}
            aria-current={index === safeCurrentSlide ? 'true' : undefined}
            data-testid="carousel-bar-segment"
          />
        ))}
      </div>
    );
  };

  const renderArrow = (direction: 'left' | 'right') => {
    const isLeft = direction === 'left';
    const isDisabled = isLeft ? isFirstSlide : isLastSlide;
    const imageOverlayClass = type === MCarouselControlsType.DotsWithOverlay && imageOverlay ? ' image-overlay' : '';

    return (
      <div
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        className={`m-carousel-control-${direction}-arrow${imageOverlayClass}`}
        aria-label={isLeft ? 'Previous' : 'Next'}
        aria-disabled={isDisabled}
        onClick={isLeft ? handlePrev : handleNext}
        onKeyDown={handleKeyDown(direction)}
        data-testid={`carousel-arrow-${direction}`}
      >
        <span className={`icon-arrow-${direction}`} />
      </div>
    );
  };

  const renderControls = () => (
    <>
      {renderArrow('left')}
      {type === MCarouselControlsType.Bar ? renderBar() : renderDots()}
      {renderArrow('right')}
    </>
  );

  return (
    <div className={wrapperClass}>
      {type === MCarouselControlsType.DotsWithOverlay ? (
        <div className="m-carousel-control-overlay-wrapper">{renderControls()}</div>
      ) : (
        renderControls()
      )}
    </div>
  );
};

export { MCarouselControlsType } from './MCarouselControls.types';
export type { MCarouselControlsProps } from './MCarouselControls.types';
